/* CatalogTechApp.jsx — the Catalog Technician "LHC OS" workspace at /catalog,
 * pixel-per-pixel port of the prototype's renderCatalog() shell
 * (LHC_Catalog_Technician_trimmed HTML 56206-56274): exact class names, the
 * 96px/210px #lhcos grid, top bar + product-family tabs, left rail, command
 * palette, theme toggle and the ui.osView router.
 *
 * Data flow (cat-arch.md §3):
 *   boot: GET /api/catalog/state → normalize() (in-memory schema-4 semantics)
 *         → store.init(blob, user) → first mirror write (_v:2, owner-tagged)
 *   edits: store.mutate(fn) → sync mirror + debounced PUT (see store.js)
 * Views are lazy chunks; the .osmain scroller is never remounted across
 * view-internal updates (controlled inputs + stable shell), which preserves
 * scroll/focus the way the prototype's scrollTop/caret hacks did. */
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useCatalog } from "./store";
import { fetchState, normalize, collectDataUrlTexRefs, uploadDataUrlAsset } from "./lib/persistence";
import { FAM, MATLIB } from "./data";
import { isFurnitureLine, pageVisibleForLine, lineForFabricPage, redirectForLine } from "./lib/railFamilies";
import { Toast } from "./components/primitives";
import { useI18n } from "@/i18n";
import LanguagePicker from "../i18n/LanguagePicker";
import "./catalog-os.css";

/* ── view error boundary (2026-08-06 trust round) ──────────────────────────
 * Before this, a throw in any lazy view unmounted the WHOLE React tree to a
 * blank white page. The boundary keeps the shell alive and offers recovery. */
class ViewBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { try { console.error("[LHC OS] view crashed:", err); } catch (e) { /* noop */ } }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div style={{ padding: 40, maxWidth: 560 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This page hit an error</div>
        <div className="mut" style={{ fontSize: 13, marginBottom: 6 }}>Your catalog data is safe — the last saved state is on the server.</div>
        <div className="mono" style={{ fontSize: 11, opacity: 0.7, marginBottom: 14, wordBreak: "break-all" }}>{String(this.state.err).slice(0, 300)}</div>
        <button className="osbtn" onClick={() => { this.setState({ err: null }); useCatalog.getState().setUi({ osView: "dashboard" }); }}>Back to Dashboard</button>{" "}
        <button className="osbtn" onClick={() => window.location.reload()}>Reload app</button>
      </div>
    );
  }
}

/* Views are code-split — the shell stays light (App.js imports us eagerly). */
const DashView = lazy(() => import("./views/DashView"));
const ProductsView = lazy(() => import("./views/ProductsView"));
const MaterialsView = lazy(() => import("./views/MaterialsView"));
/* 2026-08-04 user mandate: the Component Registry PAGE is removed from the
 * product (EntityLibraryView unmounted). The entity DATA layer stays — it
 * silently powers the compatibility engine, entity components and the
 * server-side per-entity persistence. */
const ThreeDView = lazy(() => import("./views/ThreeDView"));
const SkuBenchView = lazy(() => import("./views/SkuBenchView"));
const FinishesView = lazy(() => import("./views/FinishesView"));
const AccessoriesView = lazy(() => import("./views/AccessoriesView"));
const HandlesView = lazy(() => import("./views/GolaHandlesViews").then((m) => ({ default: m.HandlesView })));
const GolaView = lazy(() => import("./views/GolaHandlesViews").then((m) => ({ default: m.GolaView })));
const SidePanelsView = lazy(() => import("./views/GolaHandlesViews").then((m) => ({ default: m.SidePanelsView })));
const PlinthView = lazy(() => import("./views/PlinthView"));
const LightingView = lazy(() => import("./views/LightingView"));
const MatListView = lazy(() => import("./views/MatListView"));
const FabricView = lazy(() => import("./views/FabricView"));
const EquipmentView = lazy(() => import("./views/EquipmentView"));
const PricingView = lazy(() => import("./views/StaticViews").then((m) => ({ default: m.PricingView })));
const ImportView = lazy(() => import("./views/StaticViews").then((m) => ({ default: m.ImportView })));
const CarcImportView = lazy(() => import("./views/CarcImportView"));
const ProdImportView = lazy(() => import("./views/ProdImportView"));
const ImportWizardModal = lazy(() => import("./components/ImportWizardModal"));

const FABRIC_ZONE = { "fabric-in": "indoor", "fabric-lt": "leather", "fabric-out": "outdoor", "fabric-rp": "rope" };

/* osMain() router (HTML 59736-59775, v4.56): gola aliases to the Handles page,
 * backsplash to the (merged) Worktops page, 15 eq-* categories route through
 * EquipmentView, and Dashboard is the default view. */
function routeFor(osView) {
  if (osView === "materials") return <MaterialsView />;   // Stage 3a material library
  if (osView === "products") return <ProductsView />;
  if (osView === "sku") return <SkuBenchView />;
  if (osView === "threed") return <ThreeDView />;
  if (osView === "finishes") return <FinishesView />;
  if (osView === "gola") return <GolaView />;      // own page since 2026-08-09
  if (osView === "side-panels") return <SidePanelsView />;   // 2026-08-29 fittings: decorative end panels
  if (osView === "handles") return <HandlesView />;
  if (osView === "plinth") return <PlinthView />;
  if (osView === "lighting") return <LightingView />;
  if (osView === "worktops" || osView === "backsplash") {
    // v4.56 osWorktops (HTML 58776): Worktops + Backsplash pages concatenated.
    return (<><MatListView kind="worktops" /><MatListView kind="backsplash" /></>);
  }
  if (FABRIC_ZONE[osView]) return <FabricView zone={FABRIC_ZONE[osView]} />;
  if (osView.indexOf("eq-") === 0) return <EquipmentView cat={osView.slice(3)} />;
  if (osView === "accessories" || osView === "pglib") return <AccessoriesView />;
  if (osView === "pricing") return <PricingView />;
  if (osView === "carcimp") return <CarcImportView />;
  if (osView === "prodimp") return <ProdImportView />;
  if (osView === "import") return <ImportView />;
  return <DashView />;
}

/* Views that render a pinned .pimnav — .osmain gets the osmain-nav class and
 * the measured sticky offsets (prototype osPinOffsets, HTML 59845-59862). */
const PIMNAV_VIEWS = { products: 1, threed: 1 };

/* Rail foot + dash quick-action both export the bundle via the lazy chunk. */
function exportBundle() {
  import("./lib/exports").then((m) => m.exportCatalogBundle(useCatalog.getState().blob));
}

export default function CatalogTechApp() {
  const { user, logout } = useAuth();
  // hooks must run before any early return (rules-of-hooks)
  const { t } = useI18n();
  const navigate = useNavigate();

  const blob = useCatalog((s) => s.blob);
  const ui = useCatalog((s) => s.ui);
  const init = useCatalog((s) => s.init);
  const setUi = useCatalog((s) => s.setUi);
  const notify = useCatalog((s) => s.notify);
  const mutate = useCatalog((s) => s.mutate);   // Stage 4 tex sweeper

  const palInRef = useRef(null);
  const [palQ, setPalQ] = useState("");
  const [bootError, setBootError] = useState(null);
  const [bootTry, setBootTry] = useState(0);

  /* ── boot: fetch → normalize → init (init writes the first mirror).
   * A failed fetch used to leave a permanent "Loading catalog…" dead end —
   * now it renders an error screen with a real Retry (2026-08-06). ───────── */
  useEffect(() => {
    let alive = true;
    setBootError(null);
    fetchState()
      .then((data) => {
        if (!alive) return;
        const saved = data && data.blob;
        // did the SAVED tenant already carry the component registry? (drives
        // the one-shot adoption save inside init)
        const hadRegistry = !!(saved && saved.entities && Object.keys(saved.entities).length);
        init(normalize(saved), user, hadRegistry);
        // deep link: /catalog?v=<view>&sku=<sku> restores the view AND the
        // open product/bench on refresh (2026-08-24 "where I am must stay
        // where I am" — ?v=sku without the sku bounced back to Products).
        try {
          const q = new URLSearchParams(window.location.search);
          const v = q.get("v");
          const sku = q.get("sku");
          const patch = {};
          if (v) patch.osView = v;
          if (sku) patch.osSku = sku;
          if (Object.keys(patch).length) useCatalog.getState().setUi(patch);
        } catch (e) { /* URL parsing is cosmetic */ }
      })
      .catch((e) => { if (alive) setBootError(String((e && e.message) || e || "load failed")); });
    return () => { alive = false; };
    // re-runs only via the Retry button (bootTry) — ProtectedRoute guarantees
    // `user` is resolved here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootTry]);

  /* ── DATA-URL TEXTURE SWEEPER (Stage 4 hygiene, 2026-08-04) ──────────────
   * Embedded base64 images in the blob (historical door/carcass/gola/plinth/
   * fabric swatches, entity thumbs, generated melamine textures) bloat the
   * 16MB-limited catalog doc + the localStorage mirror. Whenever any exist,
   * move them to /uploads and store the URL instead. Self-healing + safe:
   *  · value RE-CHECKED at replace time (a shifted array index can never
   *    overwrite a non-data-URL value);
   *  · a failed upload is remembered per path and never retried this session;
   *  · one batch per pass — the blob-change re-render sweeps the remainder. */
  const sweepRef = useRef({ running: false, failed: new Set() });
  useEffect(() => {
    if (!blob || !blob._v) return;                   // not booted yet
    const sw = sweepRef.current;
    if (sw.running) return;
    const refs = collectDataUrlTexRefs(blob).filter((r) => !sw.failed.has(r.path.join(".")));
    if (!refs.length) return;
    sw.running = true;
    (async () => {
      const done = [];
      for (const r of refs.slice(0, 40)) {
        try {
          const cur = r.path.reduce((o, k) => (o == null ? o : o[k]), useCatalog.getState().blob);
          if (typeof cur !== "string" || cur.indexOf("data:image/") !== 0) continue;
          const url = await uploadDataUrlAsset(cur);
          if (url) done.push({ path: r.path, url });
          else sw.failed.add(r.path.join("."));
        } catch (e) { sw.failed.add(r.path.join(".")); }
      }
      if (done.length) {
        mutate((b) => {
          done.forEach(({ path, url }) => {
            let o = b;
            for (let i = 0; i < path.length - 1; i++) { o = o == null ? o : o[path[i]]; }
            const k = path[path.length - 1];
            if (o && typeof o[k] === "string" && o[k].indexOf("data:image/") === 0) o[k] = url;
          });
        });
        notify("Moved " + done.length + " embedded texture" + (done.length === 1 ? "" : "s") + " to file storage");
      }
      // ops visibility — the sweep must never fail silently
      // eslint-disable-next-line no-console
      console.info("[LHC TEX SWEEP]", JSON.stringify({ found: refs.length, moved: done.length, failed: sw.failed.size }));
      sw.running = false;
    })();
  }, [blob, mutate, notify]);

  /* v4.56 LHCMat contract (cabinet.html 58029): room-level material bridge —
   * the wizard (and any same-page consumer) reads the LIVE catalog-derived
   * material categories. Published as the prototype's window global so the
   * contract matches cabinet.html byte-for-byte for consumers. */
  useEffect(() => {
    let alive = true;
    import("./lib/wizardBridge").then((m) => {
      if (!alive) return;
      window.LHCMat = {
        rooms: () => {
          const b = useCatalog.getState().blob;
          return b ? m.buildWizardMaterialCats(b) : [];
        },
      };
    });
    return () => { alive = false; delete window.LHCMat; };
  }, []);

  /* THEME (2026-08-06 usability-audit A19): the technician is LOCKED to its
   * dark OS look. The audit verified the light overrides cover only the shell
   * chrome — dashboards, libraries and the bench keep hardcoded dark panels,
   * so following a light platform theme produced broken dark-on-light pages.
   * The rest of the platform keeps the ONE theme engine untouched. */

  /* URL ⇄ view sync (2026-08-06): the router is zustand state under a splat
   * route, so Back used to exit the app and refresh always lost the view.
   * go() pushes history entries; this keeps the query string current for
   * refresh/bookmarks; popstate restores the view for Back/Forward. */
  useEffect(() => {
    const onPop = (e) => {
      const q = new URLSearchParams(window.location.search);
      const v = (e.state && e.state.osView) || q.get("v") || "dashboard";
      const sku = (e.state && e.state.osSku) || q.get("sku") || null;
      useCatalog.getState().setUi({ osView: v, osSku: sku, paletteOpen: false });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  useEffect(() => {
    if (!blob) return;
    try {
      const url = new URL(window.location.href);
      const wantSku = ui.osView === "sku" ? (ui.osSku || "") : "";
      if (url.searchParams.get("v") !== ui.osView || (url.searchParams.get("sku") || "") !== wantSku) {
        url.searchParams.set("v", ui.osView);
        if (wantSku) url.searchParams.set("sku", wantSku); else url.searchParams.delete("sku");
        window.history.replaceState({ osView: ui.osView, osSku: wantSku }, "", url.pathname + url.search);
      }
    } catch (e) { /* cosmetic */ }
  }, [ui.osView, ui.osSku, blob]);

  /* batch 14 (2026-10-03): a page hidden for the current product line never stays on screen —
   * a fabric deep link opens its own line (Indoor / Outdoor); a cabinet library page while
   * Indoor / Outdoor is on goes to the dashboard (lib/railFamilies is the one rule). */
  useEffect(() => {
    const v = ui.osView;
    const fl = lineForFabricPage(v);
    if (fl && !isFurnitureLine(ui.osLine)) { setUi({ osLine: fl, osCat: fl, osSub: "all", osType: "all", pimSel: {} }); return; }
    const to = redirectForLine(v, ui.osLine);
    if (to && to !== v) setUi({ osView: to });
  }, [ui.osView, ui.osLine, setUi]);

  /* ⌘K / Ctrl+K toggles the palette, Esc closes (HTML 54667). */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        const s = useCatalog.getState();
        s.setUi({ paletteOpen: !s.ui.paletteOpen });
      }
      if (e.key === "Escape") useCatalog.getState().setUi({ paletteOpen: false });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* focus the palette input on open (prototype osOpenPal, 30ms). */
  useEffect(() => {
    if (!ui.paletteOpen) return undefined;
    const t = setTimeout(() => { if (palInRef.current) palInRef.current.focus(); }, 30);
    return () => clearTimeout(t);
  }, [ui.paletteOpen]);

  /* osPinOffsets (HTML 59845-59873): the pinned stack is nav → prodbar →
   * table group header; measure the real boxes so the sticky offsets tile
   * exactly. The prototype re-measures after every synchronous render; our
   * views mount LAZILY (Suspense), so a one-shot measure runs too early and
   * a stale/too-large --pin-hdr pushes the thead below its natural position
   * (the "header in the middle of the table" bug). A MutationObserver on the
   * main scroller re-measures whenever the view's DOM actually changes. */
  useEffect(() => {
    // #os-main only exists once the blob has loaded (the boot branch renders a
    // loader without it) — gate on blob so the observer actually attaches.
    const main = document.getElementById("os-main");
    if (!main) return undefined;
    let raf = 0;
    const measure = () => {
      try {
        const nav = main.querySelector(":scope > .pimnav") || main.querySelector(".pimnav");
        const bar = main.querySelector(":scope > .os-prodbar") || main.querySelector(".os-prodbar");
        const grp = main.querySelector(".prodtbl table.dg thead tr.dggrp");
        const navH = nav ? (nav.offsetHeight || 129) : 0;
        const barH = bar ? (bar.offsetHeight || 48) : 0;
        const grpH = grp ? (grp.offsetHeight || 33) : 33;
        main.style.setProperty("--pin-nav", navH + "px");
        main.style.setProperty("--pin-bar", (navH + barH) + "px");
        main.style.setProperty("--pin-hdr", (navH + barH + grpH) + "px");
      } catch (e) { /* measuring is cosmetic */ }
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(main, { childList: true, subtree: true });
    let t = null;
    const onResize = () => { clearTimeout(t); t = setTimeout(measure, 120); };
    window.addEventListener("resize", onResize);
    return () => { mo.disconnect(); cancelAnimationFrame(raf); clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [blob]);

  if (!blob) {
    return (
      <div id="lhcos" data-os-theme="dark">
        <div className="osmain" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          {bootError ? (
            <div style={{ textAlign: "center", maxWidth: 420 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Could not load the catalog</div>
              <div className="mut" style={{ fontSize: 12, marginBottom: 14, wordBreak: "break-all" }}>{bootError}</div>
              <button className="osbtn" data-boot-retry onClick={() => setBootTry((n) => n + 1)}>Retry</button>{" "}
              <button className="osbtn" onClick={() => logout().finally(() => navigate("/login"))}>Exit</button>
            </div>
          ) : (
            <span className="mut">Loading catalog…</span>
          )}
        </div>
        <Toast />
      </div>
    );
  }

  const productsCount = blob.carcass.length;

  const go = (v) => {
    // one history entry per rail navigation → browser Back walks views
    // instead of exiting the app (2026-08-06)
    try { window.history.pushState({ osView: v }, "", "/catalog?v=" + encodeURIComponent(v)); } catch (e) { /* cosmetic */ }
    setUi({ osView: v, paletteOpen: false });
    setPalQ("");
  };

  /* product-family tab click (HTML 56245): sets the line + PIM filters and
   * jumps to the Catalog view unless already on products/threed. The bulk
   * selection is index-keyed — clear it so a family switch can never leave
   * stale positional picks behind (usability-audit A5). */
  const onProd = (cat) => {
    const line = FAM[cat] || "kitchens";
    const patch = { osLine: line, osCat: line, osSub: "all", osType: "all", pimSel: {} };
    if (ui.osView !== "products" && ui.osView !== "threed") patch.osView = "products";
    setUi(patch);
  };

  /* ── save-state chip (usability-audit A1): the store's dirty→saving→saved→
   * error machine finally has a face. Error is sticky + offers a real retry
   * (a no-op mutate re-schedules the debounced PUT with the current blob). */
  const SaveChip = () => {
    const st = ui.saveState;
    if (st === "error") {
      return (
        <span data-save-state="error" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 8 }}>
          <span style={{ color: "#f87171", fontSize: 12, fontWeight: 700 }}>⚠ {t("tech.save.failed")}</span>
          <button className="osbtn" data-save-retry style={{ borderColor: "#f87171", color: "#f87171" }}
            onClick={() => mutate(() => { /* re-schedule the save with the current blob */ })}>
            {t("tech.save.retry")}
          </button>
        </span>
      );
    }
    const txt = st === "saving" ? t("tech.save.saving")
      : st === "dirty" ? t("tech.save.unsaved")
      : st === "saved" ? t("tech.save.saved") : "";
    return txt
      ? <span data-save-state={st} className="mut" style={{ fontSize: 12, marginRight: 8, color: st === "saved" ? "#22c55e" : undefined }}>{txt}</span>
      : null;
  };

  const furniture = isFurnitureLine(ui.osLine);   // batch 14: Indoor / Outdoor → the Fabric Library only

  /* nav(view,label,count?) (HTML 56213). */
  const Nav = ({ v, label, count }) => (
    <div className={"osnav" + (ui.osView === v ? " on" : "")} data-os={v} onClick={() => go(v)}>
      {label}{count != null ? <span className="c">{count}</span> : null}
    </div>
  );

  const Prod = ({ cat, label }) => (
    <button className={"osprod" + (ui.osLine === FAM[cat] ? " on" : "")} data-osprod={cat} onClick={() => onProd(cat)}>
      {label}
    </button>
  );

  return (
    <div id="lhcos" data-os-theme="dark">
      {/* ── top bar (HTML 59786-59790, v4.56: brand + version, tabs, Exit) ── */}
      <div className="ostop">
        <div className="oslogo">
          <span className="oslogo-brand"><b>LHC</b><i>STUDIO</i></span>
          <span className="osver">v4.56</span>
        </div>
        <div className="osprodnav">
          <Prod cat="kitchen" label={t("tech.top.kitchen")} />
          <Prod cat="wardrobe" label={t("tech.top.systems")} />
          <Prod cat="sofa" label={t("tech.top.indoor")} />
          <Prod cat="outdoor" label={t("tech.top.outdoor")} />
        </div>
        <SaveChip />
        {/* Language — THE platform LanguagePicker (V5: one picker everywhere;
            the hand-rolled select this replaced duplicated its logic). */}
        <LanguagePicker compact style={{ marginRight: 8 }} />
        {/* Exit = logout → /login (cat-integ.md §3 — CATALOG_MANAGER has no
          * /store access, so navigating there would bounce to /forbidden). */}
        <button className="osbtn" data-wsp-home="" onClick={() => logout().finally(() => navigate("/login"))}>{t("tech.top.exit")}</button>
        <div className="osav">LH</div>
      </div>

      {/* ── left rail (HTML 59791-59804, v4.56: Main + MATLIB-driven groups) ── */}
      <div className="osrail">
        <div className="osgrp">{t("tech.nav.main")}</div>
        <div className="osnavbox osnavbox-pri"><Nav v="dashboard" label={t("tech.nav.dashboard")} /></div>
        <div className="osgrp">{t("tech.nav.catalogSection")}</div>
        <div className="osnavbox osnavbox-pri"><Nav v="products" label={t("tech.nav.catalog")} count={productsCount} /></div>
        <div className="osnavbox osnavbox-pri"><Nav v="threed" label={t("tech.nav.products")} /></div>
        {/* batch 14 (2026-10-03): the material libraries follow the top-bar line — Kitchen /
            Systems keep the cabinet libraries, Indoor / Outdoor show ONLY their Fabric Library */}
        {furniture ? (<>
          <div className="osgrp" data-rail-section="fabric">{t("tech.nav.fabricLibrary")}</div>
          <div className="osnavbox">
            {ui.osLine === "indoor" ? (<>
              <Nav v="fabric-in" label={t("tech.nav.indoorFabric")} />
              <Nav v="fabric-lt" label={t("tech.nav.leather")} />
            </>) : (<>
              <Nav v="fabric-out" label={t("tech.nav.outdoorFabric")} />
              <Nav v="fabric-rp" label={t("tech.nav.rope")} />
            </>)}
          </div>
        </>) : (<>
        <div className="osgrp" data-rail-section="materials">{t("tech.nav.materialLibrary")}</div>
        {/* Stage 3a: the PBR material library (gallery + my library + upload) */}
        <div className="osnavbox osnavbox-pri"><Nav v="materials" label={t("tech.nav.materials")} count={(blob.materials || []).length || null} /></div>
        <div className="osnavbox">
          {/* MATLIB pages keep their ids; the LABEL comes from the catalogs
              (finishes→Carcasses, accessories→Fronts — v4.56 page naming). */}
          {MATLIB.map((m) => <Nav key={m.page} v={m.page}
            label={t("tech.nav." + ({ finishes: "carcasses", accessories: "fronts", handles: "handles", gola: "gola", plinth: "plinth", lighting: "lighting", worktops: "worktops" }[m.page] || m.page))} />)}
        </div>
        <div className="osgrp" data-rail-section="equipment">{t("tech.nav.equipmentLibrary")}</div>
        <div className="osnavbox">
          <Nav v="eq-sinks" label={t("tech.nav.sinks")} />
          <Nav v="eq-taps" label={t("tech.nav.taps")} />
          <Nav v="eq-hobs" label={t("tech.nav.hobs")} />
          <Nav v="eq-ovens" label={t("tech.nav.ovens")} />
          <Nav v="eq-hoods" label={t("tech.nav.hoods")} />
          <Nav v="eq-fridges" label={t("tech.nav.fridges")} />
          <Nav v="eq-dishwashers" label={t("tech.nav.dishwashers")} />
        </div>
        {/* FITTINGS (usability-audit B5): the 8 categories that actually power
            the configurator's interior/runner options were buried as scroll
            sections inside the Carcasses page — now first-class rail pages. */}
        <div className="osgrp">{t("tech.nav.fittingsLibrary")}</div>
        <div className="osnavbox">
          <Nav v="eq-shelves" label={t("tech.nav.eqShelves")} />
          <Nav v="eq-drawers" label={t("tech.nav.eqDrawers")} />
          <Nav v="eq-organizers" label={t("tech.nav.organizers")} />
          <Nav v="eq-bins" label={t("tech.nav.bins")} />
          <Nav v="eq-corners" label={t("tech.nav.corners")} />
          <Nav v="eq-pullouts" label={t("tech.nav.pullouts")} />
          <Nav v="eq-columns" label={t("tech.nav.columns")} />
          <Nav v="eq-hinges" label={t("tech.nav.hinges")} />
          <Nav v="side-panels" label={t("tech.nav.sidePanels")} />
        </div>
        </>)}
        <div className="osrail-foot">
          <button className="osbtn" id="os-bundle" onClick={exportBundle}>{t("tech.nav.bundle")}</button>
        </div>
      </div>

      {/* ── main view (never remounted across view-internal updates) ─────── */}
      <div className={"osmain" + (PIMNAV_VIEWS[ui.osView] ? " osmain-nav" : "")} id="os-main">
        <ViewBoundary key={ui.osView}>
          <Suspense fallback={<div className="mut" style={{ padding: 30, fontSize: 13 }}>{t("tech.loading")}</div>}>{routeFor(ui.osView)}</Suspense>
        </ViewBoundary>
      </div>

      {/* ── command palette — WIRED (usability-audit A13: the input used to be
          a decoration). Filters nav destinations + products by SKU/name;
          Enter runs the first hit; a product hit opens its bench. ─────────── */}
      <div
        className={"osscrim" + (ui.paletteOpen ? " show" : "")}
        id="os-scrim"
        onClick={(e) => { if (e.target === e.currentTarget) setUi({ paletteOpen: false }); }}
      >
        <div className="ospal">
          {(() => {
            if (!ui.paletteOpen) return null;
            const q = palQ.trim().toLowerCase();
            const NAVS = [
              ["products", t("tech.nav.catalog")], ["threed", t("tech.nav.products")], ["dashboard", t("tech.nav.dashboard")],
              ["materials", t("tech.nav.materials")], ["finishes", t("tech.nav.carcasses")], ["accessories", t("tech.nav.fronts")],
              ["handles", t("tech.nav.handles")], ["plinth", t("tech.nav.plinth")], ["lighting", t("tech.nav.lighting")],
              ["worktops", t("tech.nav.worktops")], ["pricing", t("tech.pal.pricing")], ["import", t("tech.pal.import")],
              ["eq-shelves", t("tech.nav.eqShelves")], ["eq-drawers", t("tech.nav.eqDrawers")], ["eq-organizers", t("tech.nav.organizers")],
              ["eq-bins", t("tech.nav.bins")], ["eq-corners", t("tech.nav.corners")], ["eq-pullouts", t("tech.nav.pullouts")],
              ["eq-columns", t("tech.nav.columns")], ["eq-hinges", t("tech.nav.hinges")],
              ["eq-sinks", t("tech.nav.sinks")], ["eq-taps", t("tech.nav.taps")], ["eq-hobs", t("tech.nav.hobs")],
              ["eq-ovens", t("tech.nav.ovens")], ["eq-hoods", t("tech.nav.hoods")], ["eq-fridges", t("tech.nav.fridges")],
              ["eq-dishwashers", t("tech.nav.dishwashers")],
              ["fabric-in", t("tech.nav.indoorFabric")], ["fabric-lt", t("tech.nav.leather")],
              ["fabric-out", t("tech.nav.outdoorFabric")], ["fabric-rp", t("tech.nav.rope")],
            ].filter(([v]) => pageVisibleForLine(v, ui.osLine));
            const navHits = (q ? NAVS.filter(([, l]) => String(l).toLowerCase().indexOf(q) >= 0) : NAVS.slice(0, 3)).slice(0, 6);
            const prodHits = q
              ? blob.carcass.filter((u) => u && u.sku && (
                  u.sku.toLowerCase().indexOf(q) >= 0 || String(u.name || "").toLowerCase().indexOf(q) >= 0
                )).slice(0, 8)
              : [];
            const openProduct = (u) => { setUi({ osView: "sku", osSku: u.sku, paletteOpen: false }); setPalQ(""); };
            const runFirst = () => {
              if (prodHits.length) return openProduct(prodHits[0]);
              if (navHits.length) return go(navHits[0][0]);
            };
            return (
              <>
                <div className="in">
                  <input id="os-pal-in" ref={palInRef} placeholder={t("tech.pal.placeholder")} autoComplete="off"
                    value={palQ} onChange={(e) => setPalQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runFirst(); } }} />
                </div>
                {prodHits.length ? <div className="g">{t("tech.pal.productsGroup")}</div> : null}
                {prodHits.map((u) => (
                  <div className="r" key={u.sku} data-pal-sku={u.sku} onClick={() => openProduct(u)}>
                    <span><span className="mono" style={{ marginRight: 8 }}>{u.sku}</span>{u.name || ""}</span>
                    <span className="k">{u.inCatalog && u.status === "live" ? "live" : (u.status || "—")}</span>
                  </div>
                ))}
                <div className="g">{t("tech.pal.jumpTo")}</div>
                {navHits.map(([v, l]) => (
                  <div className="r" key={v} data-os={v} onClick={() => go(v)}>
                    <span>{l}</span>{v === "products" ? <span className="k">{productsCount}</span> : null}
                  </div>
                ))}
                {q && !prodHits.length && !navHits.length ? (
                  <div className="g">{t("tech.pal.noMatches")}</div>
                ) : null}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── overlays ─────────────────────────────────────────────────────── */}
      {ui.wizard ? (
        <Suspense fallback={null}><ImportWizardModal /></Suspense>
      ) : null}
      <Toast />
    </div>
  );
}
